// Top-level views: the Competitions grid and a single Competition's seasons list.

import React, { useState } from 'react'
import { CircularProgress } from '@material-ui/core'
import TOKENS from './tokens'
import { Icon, Badge, Button, IconBtn, SectionTitle, SeasonSyncBadge, useHoverCard } from './Primitives'
import { fmtDateShort, seasonTone, prettyEnum } from './helpers'
import { isLiteStatsMode } from '../../../utils/constants/statsModeOptions'

const PAGE = { padding: 24, maxWidth: 1240, margin: '0 auto' }

/* ----------------------------------------------------- Competitions grid -- */

export const CompetitionsView = ({ competitions = [], isLoading, onOpen, onCreate, onEdit, onDelete }) => {
  const hover = useHoverCard()
  const [query, setQuery] = useState('')
  const filtered = competitions.filter((c) => (c.name || '').toLowerCase().includes(query.toLowerCase()))

  return (
    <div style={PAGE}>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, letterSpacing: '-0.02em', color: TOKENS.text }}>Competitions</h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: TOKENS.text3 }}>
            Manage every league, cup and tournament in your federation.
          </p>
        </div>
        {onCreate && (
          <Button variant="primary" icon="plus" onClick={onCreate}>
            New Competition
          </Button>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <h2 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: TOKENS.text }}>All competitions</h2>
          <Badge>{competitions.length}</Badge>
        </div>
        <div style={{ position: 'relative' }}>
          <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: TOKENS.text3 }}>
            <Icon name="search" size={13} />
          </span>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter…"
            style={{
              height: 32,
              padding: '0 12px 0 28px',
              fontSize: 12,
              fontFamily: TOKENS.font,
              background: TOKENS.white,
              border: `1px solid ${TOKENS.border}`,
              borderRadius: TOKENS.radiusSm,
              width: 180,
              outline: 'none',
            }}
          />
        </div>
      </div>

      {isLoading ? (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <CircularProgress style={{ color: TOKENS.coral }} />
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, alignItems: 'start' }}>
          {filtered.map((c) => {
            const seasons = Array.isArray(c.seasons) ? c.seasons : null
            return (
              <div
                key={c.id}
                className={hover.card}
                onClick={() => onOpen(c)}
                style={{
                  textAlign: 'left',
                  background: TOKENS.white,
                  border: `1px solid ${TOKENS.border}`,
                  borderRadius: TOKENS.radius,
                  padding: 20,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: seasons ? 16 : 4 }}>
                  {c.logoUrl ? (
                    <img src={c.logoUrl} alt={c.name} style={{ width: 48, height: 48, borderRadius: TOKENS.radius, objectFit: 'contain', flexShrink: 0, background: TOKENS.surface2 }} />
                  ) : (
                    <div style={{ width: 48, height: 48, borderRadius: TOKENS.radius, display: 'flex', alignItems: 'center', justifyContent: 'center', color: TOKENS.white, flexShrink: 0, background: `linear-gradient(135deg, ${TOKENS.coral}, ${TOKENS.amber})` }}>
                      <Icon name="trophy" size={22} />
                    </div>
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h3 style={{ margin: '0 0 2px', fontSize: 15, fontWeight: 600, color: TOKENS.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</h3>
                    <div style={{ fontSize: 12, color: TOKENS.text3, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span>Basketball</span>
                      {c.gender && <><span style={{ width: 3, height: 3, borderRadius: 999, background: TOKENS.text3 }} /><span>{prettyEnum(c.gender)}</span></>}
                      {c.ageRestriction && <><span style={{ width: 3, height: 3, borderRadius: 999, background: TOKENS.text3 }} /><span>{prettyEnum(c.ageRestriction)}</span></>}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 2 }} onClick={(e) => e.stopPropagation()}>
                    {onEdit && <IconBtn icon="edit" tone="coral" title="Edit competition" onClick={() => onEdit(c)} />}
                    {onDelete && <IconBtn icon="trash" tone="danger" title="Delete competition" onClick={() => onDelete(c)} />}
                  </div>
                </div>

                {seasons && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, paddingTop: 12, borderTop: `1px solid ${TOKENS.border}` }}>
                    <Stat label="Seasons" value={seasons.length} />
                    <Stat label="Stages" value={seasons.reduce((a, s) => a + (s.stages?.length || 0), 0)} />
                    <Stat label="Live now" value={seasons.filter((s) => seasonTone(s) === 'live').length} />
                  </div>
                )}
              </div>
            )
          })}

          {onCreate && (
            <button
              type="button"
              onClick={onCreate}
              style={{
                border: `2px dashed ${TOKENS.borderStrong}`,
                borderRadius: TOKENS.radius,
                padding: 16,
                alignSelf: 'stretch',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 12,
                background: 'transparent',
                color: TOKENS.text3,
                cursor: 'pointer',
                fontFamily: TOKENS.font,
              }}
            >
              <div style={{ width: 32, height: 32, borderRadius: 999, border: '2px solid currentColor', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Icon name="plus" size={16} />
              </div>
              <div style={{ textAlign: 'left' }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>Create competition</div>
                <div style={{ fontSize: 11, opacity: 0.8 }}>Start a new league, cup or tournament</div>
              </div>
            </button>
          )}
        </div>
      )}
    </div>
  )
}

const Stat = ({ label, value }) => (
  <div>
    <div style={{ fontSize: 10, color: TOKENS.text3, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>{label}</div>
    <div style={{ fontSize: 16, fontWeight: 600, color: TOKENS.text, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
  </div>
)

/* ------------------------------------------------- Single competition view -- */

export const CompetitionView = ({ competition, seasons = [], isLoading, onOpenSeason, onNewSeason, onEditCompetition, onEditSeason, onDeleteSeason }) => {
  const hover = useHoverCard()
  if (!competition) return null

  return (
    <div style={PAGE}>
      {/* Slim header */}
      <div style={{ background: TOKENS.white, border: `1px solid ${TOKENS.border}`, borderRadius: TOKENS.radius, padding: '14px 20px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
        {competition.logoUrl ? (
          <img src={competition.logoUrl} alt={competition.name} style={{ width: 40, height: 40, borderRadius: TOKENS.radiusSm, objectFit: 'contain', flexShrink: 0, background: TOKENS.surface2 }} />
        ) : (
          <div style={{ width: 40, height: 40, borderRadius: TOKENS.radiusSm, display: 'flex', alignItems: 'center', justifyContent: 'center', color: TOKENS.white, flexShrink: 0, background: `linear-gradient(135deg, ${TOKENS.coral}, ${TOKENS.amber})` }}>
            <Icon name="trophy" size={18} />
          </div>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, letterSpacing: '-0.02em', color: TOKENS.text, whiteSpace: 'nowrap' }}>{competition.name}</h1>
            {competition.gender && <Badge>{prettyEnum(competition.gender)}</Badge>}
            {competition.ageRestriction && <Badge>{prettyEnum(competition.ageRestriction)}</Badge>}
          </div>
          <p style={{ margin: 0, fontSize: 11.5, color: TOKENS.text3 }}>
            {seasons.length} season{seasons.length === 1 ? '' : 's'}
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          {onEditCompetition && (
            <Button variant="outline" icon="edit" onClick={() => onEditCompetition(competition)}>
              Edit
            </Button>
          )}
          {onNewSeason && (
            <Button variant="primary" icon="plus" onClick={onNewSeason}>
              New Season
            </Button>
          )}
        </div>
      </div>

      <SectionTitle>Seasons</SectionTitle>

      {isLoading ? (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <CircularProgress style={{ color: TOKENS.coral }} />
        </div>
      ) : seasons.length === 0 ? (
        <div style={{ background: TOKENS.white, border: `1px solid ${TOKENS.border}`, borderRadius: TOKENS.radius, padding: 32, textAlign: 'center' }}>
          <p style={{ fontSize: 13, color: TOKENS.text3, marginBottom: onNewSeason ? 16 : 0 }}>
            {onNewSeason ? 'No seasons yet. Create one to get started.' : 'No seasons yet.'}
          </p>
          {onNewSeason && <Button variant="primary" icon="plus" onClick={onNewSeason}>Add Season</Button>}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {seasons.map((s) => {
            const tone = seasonTone(s)
            const isLive = tone === 'live'
            return (
              <div
                key={s.id}
                className={hover.card}
                onClick={() => onOpenSeason(s)}
                style={{ background: TOKENS.white, border: `1px solid ${TOKENS.border}`, borderRadius: TOKENS.radius, padding: 16, display: 'flex', alignItems: 'center', gap: 20 }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 220 }}>
                  <div style={{ width: 36, height: 36, borderRadius: TOKENS.radiusSm, display: 'flex', alignItems: 'center', justifyContent: 'center', background: isLive ? TOKENS.coralSoft : TOKENS.surface2, color: isLive ? TOKENS.coralDark : TOKENS.text3 }}>
                    <Icon name="calendar" size={16} />
                  </div>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ fontSize: 15, fontWeight: 600, color: TOKENS.text }}>{s.name}</div>
                      {isLive ? <Badge tone="live" dot>Live</Badge> : tone === 'upcoming' ? <Badge tone="upcoming" dot>Upcoming</Badge> : tone === 'past' ? <Badge tone="past">Archived</Badge> : null}
                      {isLiteStatsMode(s.statsMode) && <Badge tone="coral">Lite</Badge>}
                      <SeasonSyncBadge sync={s.externalCmsSync} />
                    </div>
                    <div style={{ fontSize: 11, color: TOKENS.text3, marginTop: 2, fontFamily: TOKENS.mono }}>
                      {fmtDateShort(s.startDate)} → {fmtDateShort(s.endDate)}
                    </div>
                  </div>
                </div>
                <div style={{ flex: 1 }} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 2 }} onClick={(e) => e.stopPropagation()}>
                  {onEditSeason && <IconBtn icon="edit" tone="coral" title="Edit season" onClick={() => onEditSeason(s)} />}
                  {onDeleteSeason && <IconBtn icon="trash" tone="danger" title="Delete season" onClick={() => onDeleteSeason(s)} />}
                </div>
                <Icon name="arrowRight" size={16} style={{ color: TOKENS.text3 }} />
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
